import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import Checkbox from '@cobliteam/ui/form/Checkbox';
import Label from '@cobliteam/ui/form/Label';
import Icon from '@cobliteam/ui/icons';
import { useCallback, useEffect, useState } from 'react';

export enum InvitationStatus {
  ACCEPTED = 'ACCEPTED',
  PENDING = 'PENDING',
}

interface MultiSelectInvitationStatusProps {
  size?: 'sm' | 'md';
  selectedStatus: InvitationStatus[];
  onChange: (status: InvitationStatus[]) => void;
}

const MultiSelectInvitationStatus = ({ selectedStatus, onChange, size = 'md' }: MultiSelectInvitationStatusProps) => {
  const { t } = useTranslation('multiselects');
  const [forceClosed, setForceClosed] = useState(false);
  const [draft, setDraft] = useState(selectedStatus || []);

  const getInvitationStatusLabel = (status: InvitationStatus) => {
    switch (status) {
      case InvitationStatus.ACCEPTED:
        return t('invitation_status.active');
      case InvitationStatus.PENDING:
        return t('invitation_status.pending');
    }
  };

  const hasNotChanged = selectedStatus?.length === draft?.length && selectedStatus.every((value) => draft?.includes(value));

  const onClick = useCallback(() => {
    setDraft(selectedStatus || []);
  }, [selectedStatus]);

  const handleClear = useCallback(() => {
    setDraft([]);
  }, []);

  const handleClose = useCallback(() => {
    setForceClosed(true);
  }, []);

  const handleApply = useCallback(() => {
    onChange(draft);
    handleClose();
  }, [draft, handleClose, onChange]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  const toggleItem = useCallback(
    (statusItem: InvitationStatus) => {
      const existItem = draft.includes(statusItem);
      const newDraft = existItem ? draft.filter((value) => value !== statusItem) : [...draft, statusItem];
      setDraft(newDraft);
    },
    [draft, setDraft]
  );

  return (
    <Dropdown
      portal={false}
      onOpenChange={onClick}
      forceClose={forceClosed}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='px-2'>
            <Button variant='outlined' size='sm' disabled={draft?.length === 0} onClick={handleClear}>
              {t('invitation_status.clear_selection')}
            </Button>
          </Dropdown.Header>
          <Dropdown.Body className='px-2'>
            <Dropdown.List
              heightItem={30}
              data={Object.values(InvitationStatus)}
              loadingComponent={<Dropdown.Loading />}
              itemComponent={(item: InvitationStatus) => (
                <div className='flex items-center gap-2'>
                  <Checkbox id={item} key={item} checked={draft.includes(item)} onCheckedChange={() => toggleItem(item)} />
                  <Label htmlFor={item}>{getInvitationStatusLabel(item)}</Label>
                </div>
              )}
            />
          </Dropdown.Body>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-2'>
              <Button variant='ghost' full size='sm' onClick={handleClose}>
                {t('invitation_status.cancel')}
              </Button>
              <Button size='sm' full onClick={handleApply} disabled={hasNotChanged}>
                {t('invitation_status.apply')}
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <Button size={size} variant='ghost' startIcon={<Icon name='user-round' />}>
        {t('invitation_status.title')} {(selectedStatus?.length || 0) > 0 ? `(${selectedStatus?.length})` : ''}
      </Button>
    </Dropdown>
  );
};

export default MultiSelectInvitationStatus;
